/**
 * Post Shared Utils
 *
 * Shared asynchronous helpers used across post create, edit, and preview flows.
 */

import { Patterns } from "../../constants/patterns";
import { SNSLinksUtils } from "../../sns/links/sns-links-utils";
import { FormBase } from "../../util/form-base-form";

class PostSharedUtilsForm extends FormBase {
    public getSnsLinkInputs(root: ParentNode): HTMLInputElement[] {
        return this.queryElements(root, 'input[name="sns_links[]"]', HTMLInputElement);
    }
}

const postSharedUtilsForm = new PostSharedUtilsForm();

class PostSharedUtils {
    private static readonly URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<]+/gi;

    public static waitForVisible(element: HTMLElement | null): Promise<void> {
        return new Promise((resolve) => {
            if (!element) {
                resolve();
                return;
            }

            if (element.style.display !== "none") {
                resolve();
                return;
            }

            const observer = new MutationObserver(() => {
                if (element.style.display !== "none") {
                    observer.disconnect();
                    resolve();
                }
            });

            observer.observe(element, {
                attributes: true,
                attributeFilter: ["style"],
            });
        });
    }

    public static debounce<TArgs extends unknown[], TThis>(
        func: (this: TThis, ...args: TArgs) => void,
        delay = 300,
    ): (this: TThis, ...args: TArgs) => void {
        let timeoutId: number | undefined;

        return function (this: TThis, ...args: TArgs): void {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => func.apply(this, args), delay);
        };
    }

    public static getNormalizedSnsLinksFromForm(form: HTMLFormElement | null | undefined): string[] {
        if (!(form instanceof HTMLFormElement)) {
            return [];
        }

        const inputs = postSharedUtilsForm.getSnsLinkInputs(form);
        return SNSLinksUtils.normalizeSnsLinks(inputs.map((input) => input.value).filter(Boolean));
    }

    public static normalizePostContentHtml(content: string, snsLinks: string[] = []): string {
        let cleanedContent = String(content || "");
        const normalizedSnsLinks = SNSLinksUtils.normalizeSnsLinks(snsLinks);

        for (let index = 0; index < 3; index += 1) {
            cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.ESCAPED_IFRAME_STRIP), "");
            cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.ESCAPED_IFRAME_STRIP_ALT), "");
        }

        cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.IFRAME_TAG), "");
        cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.IFRAME_SELF_CLOSING), "");
        cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.PARAGRAPH_WITH_ESCAPED_IFRAME), "");
        cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.PARAGRAPH_WITH_IFRAME), "");

        normalizedSnsLinks.forEach((url) => {
            const encodedUrl = Patterns.encodeAmpersand(url);
            cleanedContent = cleanedContent.replace(Patterns.createUrlInParagraphPattern(url), "");
            cleanedContent = cleanedContent.replace(Patterns.createUrlInParagraphPattern(encodedUrl), "");
            cleanedContent = cleanedContent.replace(Patterns.createPlainUrlPattern(url), "");
            cleanedContent = cleanedContent.replace(Patterns.createPlainUrlPattern(encodedUrl), "");
        });

        cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
        cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");

        return cleanedContent.trim();
    }

    public static getNormalizedPostContentText(content: string, snsLinks: string[] = []): string {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = PostSharedUtils.normalizePostContentHtml(content, snsLinks);

        return String(wrapper.textContent || "")
            .replace(/\u00a0/g, " ")
            .trim();
    }

    public static getCountablePostContentText(content: string, snsLinks: string[] = []): string {
        return PostSharedUtils.getNormalizedPostContentText(content, snsLinks)
            .replace(PostSharedUtils.URL_PATTERN, "")
            .replace(/\s{2,}/g, " ")
            .trim();
    }

    public static getNormalizedPostContentLength(content: string, snsLinks: string[] = []): number {
        return Array.from(PostSharedUtils.getCountablePostContentText(content, snsLinks)).length;
    }

    public static hasNormalizedPostContentText(content: string, snsLinks: string[] = []): boolean {
        return PostSharedUtils.getNormalizedPostContentText(content, snsLinks) !== "";
    }
}

export { PostSharedUtils };
